<template>
  <div
    class="px-6 py-6 bg-pure-white rounded-xl"
    :style="{ height: '34.625rem', width }"
  >
    <div class="flex items-center justify-between mb-1">
      <BlTextTitle4 text="Client turnover" class="text-slate-900" />
      <router-link
        :to="
          `/analytics/client-turnover?start_date=${dateInput.model[0].start_date}&end_date=${dateInput.model[0].end_date}`
        "
      >
        <BlInputButton type="outlined" dense label="Details" />
      </router-link>
    </div>
    <div class="flex mb-5">
      <BlInputFilter
        :activeLabel="dateActiveLabel"
        title="Date"
        :defaultValue="[defaultDate]"
        :input="dateInput"
        @changed="onChangeHandler"
      />
    </div>
    <div v-if="overviewData.length" class="flex mb-5 gap-7">
      <div
        v-for="item in overviewData"
        :key="item.label"
        class="relative h-13.5 flex items-center"
        :style="{
          width: `calc((100% - ${(overviewData.length - 1) * 1.75}rem) / ${
            overviewData.length
          })`
        }"
      >
        <BlTooltip :tooltip="`<div class='min-w-32'>${item.label}</div>`" class="flex flex-col w-full">
          <div class="self-center text-xl font-semibold text-slate-900">
            {{ item.value }}
          </div>
          <div class="text-center truncate">
            <BlTextBody2 :text="item.label" class="truncate" />
          </div>
        </BlTooltip>
        <div v-if="item.divider" class="absolute -right-3.5">
          <div class="h-13.5 w-0.5 flex-shrink-0 bg-slate-300"></div>
        </div>
      </div>
    </div>
    <div class="h-7.5 mb-5">
      <BlTextSubtitle2 text="By discharge reasons" class="text-slate-700" />
    </div>
    <div v-if="fetchLoading" class="grid h-100 place-content-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else-if="!graphData" class="grid h-100 place-content-center">
      <BlTextBody2 :text="`Data not found.`" />
    </div>
    <div v-else class="flex flex-col gap-5">
      <PieChart
        :labels="pieChartData.labels"
        :colors="pieChartData.colors"
        :data="pieChartData.data"
        width="10rem"
      />
      <div
        class="h-20 py-2.5 px-3 grid content-start gap-y-3 gap-x-6 rounded bg-slate-100 border border-slate-400"
        :class="[widthInNumber > 25 ? 'grid-cols-4' : 'grid-cols-2']"
      >
        <div
          v-for="legend in pieChartLegends"
          :key="legend.label"
          class="flex items-center justify-between gap-2"
        >
          <div class="flex items-center gap-1 truncate" :title="legend.label">
            <div
              class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
              :style="{ backgroundColor: legend.color }"
            ></div>
            <BlTextBody2 :text="legend.label" class="truncate" />
          </div>
          <div>
            <BlTextSubtitle3 :text="legend.value" class="text-slate-800" />
          </div>
        </div>
      </div>
      <div
        v-if="pieChartPageCount > 1"
        class="flex items-center justify-center"
      >
        <div
          v-for="n in pieChartPageCount"
          :key="n"
          @click="pieChartPage = n"
          class="p-1 cursor-pointer"
        >
          <div
            :class="[pieChartPage === n ? 'bg-slate-700' : 'bg-slate-500']"
            class="h-2.5 w-2.5 flex-shrink-0 rounded-full hover:bg-slate-700 transition-all duration-300"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import PieChart from "../graphs/PieChart.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "CenterOverviewClientTurnover",
  components: {
    PieChart,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTooltip,
    BlIcon,
    BlInputFilter,
    BlInputButton
  },
  props: {
    width: { type: String, default: "25rem" }
  },
  data() {
    const interval = "year";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };
    return {
      defaultDate,
      /** */
      fetchLoading: false,
      graphData: null,
      /** */
      dateInput: {
        model: [defaultDate],
        tabs: [{ name: "Date", kind: "date" }],
        dense: true
      },
      /** */
      pieChartPage: 1,
      pieChartPerPage: 4
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    searchParams() {
      return this.$route.query || {};
    },
    widthInNumber() {
      return Number(this.width.replaceAll("rem", ""));
    },
    dateActiveLabel() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;

      const sYYYY = moment(start).format("YYYY");
      const sMMM = moment(start).format("MMM");
      const sDD = moment(start).format("DD");

      const eYYYY = moment(end).format("YYYY");
      const eMMM = moment(end).format("MMM");
      const eDD = moment(end).format("DD");

      let date = "";
      if (sYYYY === eYYYY) {
        if (sMMM === eMMM) date = `${sDD} - ${eDD} ${sMMM}, ${sYYYY}`;
        else if (sMMM === "Jan" && eMMM === "Dec") date = `${sYYYY}`;
        else date = `${sDD} ${sMMM} - ${eDD} ${eMMM}, ${sYYYY}`;
      } else date = `${sYYYY} - ${eYYYY}`;
      return `
        <span class="flex items-center gap-2">
          <span>Date</span>
          <b>${date}</b>
        </span>
      `;
    },
    /*
    "overview": {
      "active": 11,
      "archived": 1,
      "at_risk_of_discharge": 3,
      "current_active_clients": 19
    },
    */
    overviewData() {
      if (!this.graphData) return [];
      const raw = this.graphData.overview;
      if (!raw) return [];
      const keys = ["active", "at_risk_of_discharge", "archived"];
      const labels = ["New active client", "New at risk of discharged client", "New discharged client"];
      function rounded(n = 0) {
        const t = n.toString();
        return t.includes(".") ? n.toFixed(1) : n;
      }
      return keys.map((key, idx) => ({
        label: labels[idx],
        value: rounded(raw[key] || 0),
        divider: idx !== keys.length - 1
      }));
    },
    /*
    "discharged_clients": {
        "graduated": 1,
        "family_or_personal_issues": 1,
        "expectations_did_not_match": 1,
        "schedule_conflict": 1,
        "moving": 1,
        "other": 1
    },
    */
    pieChartData() {
      const n = { labels: [], colors: [], data: [], legends: [] };
      if (!this.graphData) return n;
      const raw = this.graphData.discharged_clients;
      if (!raw) return n;
      const keys = [
        "graduated",
        "family_or_personal_issues",
        "expectations_did_not_match",
        "schedule_conflict",
        "moving",
        "too_expensive",
        "other"
      ];
      const labels = [
        "Graduated",
        "Family/personal issue",
        "Expectations didn't match",
        "Schedule conflict",
        "Moving",
        "Too expensive",
        "Other"
      ];
      const colors = [
        "#FFF1F3",
        "#FECDD6",
        "#FD6F8E",
        "#A11043",
        "#C01048",
        "#7F251D",
        "#E31B54"
      ];
      const data = keys.map(key => raw[key] || 0);
      const legends = keys
        .map((key, idx) => ({
          label: labels[idx],
          color: colors[idx],
          value: data[idx]
        }))
        .sort((a, b) => b.value - a.value);
      return { labels, colors, data, legends };
    },
    pieChartLegends() {
      const legends = this.pieChartData.legends;
      const start = (this.pieChartPage - 1) * this.pieChartPerPage;
      const end = start + this.pieChartPerPage;
      return legends.slice(start, end);
    },
    pieChartPageCount() {
      const legends = this.pieChartData.legends;
      const dataLength = legends.length;
      return Math.ceil(dataLength / this.pieChartPerPage) || 1;
    }
  },
  mounted() {
    if (this.widthInNumber > 25) {
      this.pieChartPerPage = 8;
    }
    this.fetchGraph();
  },
  methods: {
    ...mapActions(["actionGet"]),
    fetchGraph() {
      const start = this.dateInput.model[0].start_date;
      const end = this.dateInput.model[0].end_date;
      let params = `?start_date=${start}&end_date=${end}`;
      if (this.searchParams["branch"]) {
        params += `&branch_ids=${this.searchParams["branch"]}`;
      }
      const data = {
        url: `/api/v1/analytics/client_turnover_graph${params}`
      };
      this.fetchLoading = true;
      this.graphData = null;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchLoading = false;
        if (!success) return;
        this.graphData = data;
      });
    },
    onChangeHandler(payload) {
      payload.input.model = payload.value;
      this.fetchGraph();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/analytics/mainDashboard/ClientTurnover.vue